import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface TaskSettings {
  auto_archive_enabled: boolean;
  auto_archive_days: number;
  auto_archive_enabled_at: string | null; // ISO timestamp — only archive todos updated after this
}

const DEFAULT_SETTINGS: TaskSettings = {
  auto_archive_enabled: true,
  auto_archive_days: 7,
  auto_archive_enabled_at: null,
};

interface UseTaskSettingsOptions {
  spaceId?: string | null;
}

export function useTaskSettings(options: UseTaskSettingsOptions = {}) {
  const { spaceId } = options;
  const { user } = useAuth();
  const [settings, setSettings] = useState<TaskSettings>(DEFAULT_SETTINGS);
  const settingsRef = useRef<TaskSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    if (!user) return;

    try {
      let query = supabase
        .from('task_settings')
        .select('auto_archive_enabled, auto_archive_days, auto_archive_enabled_at');

      if (spaceId) {
        query = query.eq('space_id', spaceId);
      } else {
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query.maybeSingle();

      if (error) throw error;

      if (data) {
        const s = {
          auto_archive_enabled: data.auto_archive_enabled,
          auto_archive_days: data.auto_archive_days,
          auto_archive_enabled_at: data.auto_archive_enabled_at,
        };
        setSettings(s);
        settingsRef.current = s;
      } else {
        setSettings(DEFAULT_SETTINGS);
        settingsRef.current = DEFAULT_SETTINGS;
      }
    } catch (err) {
      console.error('Error fetching task settings:', err);
      // Fall back to defaults on error (table may not exist yet)
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, [user, spaceId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = useCallback(
    async (updates: Partial<TaskSettings>) => {
      if (!user) return;

      const current = settingsRef.current;
      const newSettings = { ...current, ...updates };

      // When enabling auto-archive, set the enabled_at timestamp
      if (updates.auto_archive_enabled === true && !current.auto_archive_enabled) {
        newSettings.auto_archive_enabled_at = new Date().toISOString();
      }

      // Optimistic update
      setSettings(newSettings);
      settingsRef.current = newSettings;

      try {
        const row = {
          auto_archive_enabled: newSettings.auto_archive_enabled,
          auto_archive_days: newSettings.auto_archive_days,
          auto_archive_enabled_at: newSettings.auto_archive_enabled_at,
          updated_at: new Date().toISOString(),
          ...(spaceId
            ? { space_id: spaceId }
            : { user_id: user.id }),
        };

        const { error } = await supabase
          .from('task_settings')
          .upsert(row, {
            onConflict: spaceId ? 'space_id' : 'user_id',
          });

        if (error) {
          console.error('Task settings upsert error:', error);
          throw error;
        }
        console.log('Task settings saved successfully:', newSettings);
      } catch (err) {
        console.error('Error updating task settings:', err);
        // Revert on error
        fetchSettings();
      }
    },
    [user, spaceId, fetchSettings]
  );

  return { settings, loading, updateSettings };
}
